import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import { Home, Calendar, CreditCard, FileText, ChevronRight, User, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function MyTenancies() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tenancies, setTenancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const isLandlord = user?.role === 'landlord' || user?.role === 'landlord_admin';

  useEffect(() => {
    loadTenancies();
  }, []);

  const loadTenancies = async () => {
    try {
      const { data } = await api.get('/tenancies');
      // Handle both array and object responses
      const tenanciesArray = Array.isArray(data) ? data : (data?.tenancies || []);
      setTenancies(tenanciesArray);
    } catch (error) {
      toast.error('Failed to load tenancies');
    } finally {
      setLoading(false);
    }
  };

  const handleTenancyClick = (tenancyId) => {
    if (isLandlord) {
      navigate(`/landlord/tenancies/${tenancyId}`);
    } else {
      navigate(`/tenancy/${tenancyId}/lifecycle`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'ending': return 'bg-orange-100 text-orange-800';
      case 'ended': return 'bg-slate-100 text-slate-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-8">
          <h1 className="text-4xl font-display font-bold text-slate-900 mb-2">My Tenancies</h1>
          <p className="text-lg text-slate-600">View and manage your rental agreements</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading tenancies...</div>
        ) : tenancies.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <Home className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold mb-2">No tenancies yet</h3>
            <p className="text-slate-600 mb-6">Your active tenancies will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tenancies.map((tenancy) => (
              <div 
                key={tenancy.id} 
                className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md hover:border-primary/30 transition-all cursor-pointer"
                onClick={() => handleTenancyClick(tenancy.id)}
                data-testid={`tenancy-card-${tenancy.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Home className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold">
                          {tenancy.property_address || tenancy.address?.line1 || `Tenancy ${tenancy.id.slice(0, 8)}...`}
                        </h3>
                        <Badge className={getStatusColor(tenancy.status)}>
                          {tenancy.status?.toUpperCase() || 'DRAFT'}
                        </Badge>
                      </div>
                      {tenancy.address && (
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {tenancy.address.city}, {tenancy.address.postcode}
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 block mb-1">Start Date</span>
                    <span className="font-medium flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {formatDate(tenancy.start_date)}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block mb-1">End Date</span>
                    <span className="font-medium flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {tenancy.end_date ? formatDate(tenancy.end_date) : 'Periodic'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block mb-1">Monthly Rent</span>
                    <span className="font-semibold text-primary flex items-center gap-1">
                      <CreditCard className="w-3.5 h-3.5" />
                      {formatCurrency(tenancy.rent_amount)}
                    </span>
                  </div>
                  {tenancy.tenants?.[0] && (
                    <div>
                      <span className="text-slate-500 block mb-1">Tenant</span>
                      <span className="font-medium flex items-center gap-1">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        {tenancy.tenants[0].first_name} {tenancy.tenants[0].last_name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}